// Die Karten im Gespraechs-Panel: Ergaenzungen (Passus) und Ersetzungen.
//
// Standen bis zum 23.09.2026 inline in VerhandlungsPassus.jsx und sahen noch
// aus wie vor der Ueberarbeitung des Abwaegens: Titel in gesperrten Versalien
// mit "01 ·" davor, auf jeder Karte eine Marke "Entwurf", Begruendung und
// Einfuegeort immer offen, acht Knoepfe in einer Reihe. Jetzt im Stil der
// Abwaegen-Karten: Nummer, Titel, Wortlaut vorn; Hinweise nur, wenn es
// etwas zu entscheiden gibt; Begruendung eingeklappt; eine ruhige Aktionszeile.
//
// Verworfenes hat eine eigene, knappe Karte - es steht gesammelt am Ende der
// Liste, damit es die offenen Entwuerfe nicht auseinanderreisst.

import React, { useState } from 'react';
import { toast } from 'sonner';
import {
  ArrowDown, ArrowUp, Check, Crosshair, Pencil, Replace, Sparkles, Trash2, Undo2, X as XIcon,
} from 'lucide-react';
import { T, karteStil, knopfStil, feldStil, markeStil, ueberzeileStil } from '../../lib/designTokens';
import { Aufklapper, NummerKreis, textKnopfStil } from './abwaegenAnzeige';
import { abweichendeParteien } from '../../lib/parteien';
// Einfuegeort, Parteien-Hinweis und Platzhalter teilen sich die Karten seit
// dem 23.09.2026 mit der mobilen Oberflaeche.
import { UEBERARBEITEN_PLATZHALTER, abweichendText, ergaenzungsOrt, passusText } from '../../lib/gespraech';
import { imVertragText, ortText } from '../../lib/imVertrag';
import { lueckenDerErgaenzung, lueckenDerErsetzung, lueckenName, teileMitLuecken } from '../../lib/luecken';

// Nur Staende, die etwas sagen, bekommen eine Marke. "Entwurf" ist der
// Normalfall und stand vorher auf jeder Karte - eine Marke, die immer da ist,
// zeigt nichts an.
const STAND_MARKE = {
  passt: { text: 'Freigegeben', schrift: T.gut, rand: T.gutRand, grund: T.gutGrund },
  ueberarbeiten: { text: 'Vorgemerkt', schrift: T.warn, rand: T.warnRand, grund: T.warnGrund },
};

function aus(gesperrt) {
  return { opacity: gesperrt ? 0.45 : 1, cursor: gesperrt ? 'default' : 'pointer' };
}

// Der gruene Strich links fuer Freigegebenes und Zugestimmtes. Als Schatten
// statt als border-left: Mischt man die Kurzform "border" aus karteStil mit
// "borderLeft" und aendert eines davon beim Neuzeichnen, setzt React die
// Randfarben zurueck (und warnt deswegen).
function mitStrich(farbe) {
  return farbe ? `inset 3px 0 0 ${farbe}, ${karteStil.boxShadow}` : karteStil.boxShadow;
}

const iconKnopfStil = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26,
  border: `1px solid ${T.randWeich}`, borderRadius: T.rKnopf, background: T.flaeche, color: T.muted,
};

// Seit 24.10.2026 (Nutzer: „etwas professioneller und weniger verspielt“) im
// Satz eines Änderungsdokuments: je Zeile links eine kleine Beschriftung
// („Stelle“, „Bisher“, „Neu“, „Offen“), rechts der Inhalt. Keine Pillen, keine
// gestrichelten Rahmen, keine farbigen Kästen - Farbe nur als dünne Linie.
const BESCHRIFTUNG_BREITE = 54;

/** Eine Zeile der Karte: Beschriftung links, Inhalt rechts. Ohne `label` nur eingerückt. */
function Zeile({ label = '', children, oben = 8 }) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: `${BESCHRIFTUNG_BREITE}px minmax(0, 1fr)`, columnGap: 12, marginTop: oben,
    }}>
      <div style={{ ...ueberzeileStil, letterSpacing: '0.12em', lineHeight: '19px' }}>{label}</div>
      <div style={{ minWidth: 0 }}>{children}</div>
    </div>
  );
}

const wortlautStil = { paddingLeft: 10, fontSize: T.tText, lineHeight: 1.65, color: T.ink };

/** Ein Hinweis in der Karte - eine dünne farbige Linie links, kein Kasten. */
function Hinweis({ ton = 'warn', titel, children, aktion = null }) {
  const farbe = ton === 'gefahr' ? T.gefahr : T.warn;
  return (
    <Zeile oben={10}>
      <div style={{
        paddingLeft: 10, borderLeft: `2px solid ${farbe}`,
        display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', rowGap: 6,
      }}>
        <div style={{ flex: '1 1 220px', minWidth: 0, fontSize: T.tMeta, color: T.body, lineHeight: 1.55 }}>
          <strong style={{ color: farbe, fontWeight: 600 }}>{titel}</strong>{' '}{children}
        </div>
        {aktion}
      </div>
    </Zeile>
  );
}

/**
 * Wo im Vertrag - immer sichtbar oben in der Karte (seit 24.10.2026, Wunsch des
 * Nutzers: „dass mir immer angezeigt wird, wo es geändert oder ersetzt wird“).
 * Vorher stand die Stelle einer Ergänzung klein und grau unten, die einer
 * Ersetzung gar nicht. Mit `onZeigen` springt die Vorschau dorthin.
 */
function StelleZeile({ wo, zusatz = '', fehlt = false, onZeigen = null }) {
  return (
    <Zeile label="Stelle" oben={0}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', rowGap: 2 }}>
        <span style={{ flex: '1 1 200px', minWidth: 0, fontSize: T.tMeta, lineHeight: '19px' }}>
          <span style={{ fontWeight: 600, color: fehlt ? T.warn : T.navy }}>{wo}</span>
          {zusatz ? <span style={{ color: T.muted }}>{` · ${zusatz}`}</span> : null}
        </span>
        {onZeigen ? (
          <button type="button" onClick={onZeigen} title="Die Stelle in der Vorschau zeigen" style={textKnopfStil}>
            <Crosshair size={12} /> In der Vorschau
          </button>
        ) : null}
      </div>
    </Zeile>
  );
}

/** Text mit hervorgehobenen Luecken „[Frist Baubeginn]“ (seit 24.10.2026). */
function TextMitLuecken({ text }) {
  return teileMitLuecken(text).map((teil, i) => (teil.luecke ? (
    <span key={i} title="Offene Angabe – unten ausfüllen oder im Gespräch nennen" style={{
      background: T.warnGrund, color: T.warn, borderBottom: `1px solid ${T.warnRand}`,
      padding: '0 2px', fontWeight: 600, whiteSpace: 'nowrap',
    }}>{teil.text}</span>
  ) : <React.Fragment key={i}>{teil.text}</React.Fragment>));
}

/**
 * Je offene Luecke ein Feld (seit 24.10.2026). Vorher liess sich „[Frist
 * Baubeginn]“ nirgends fuellen: Die KI aendert Zugestimmtes nie, und die Karte
 * hatte kein Feld - die Luecke stand in der Vorschau und waere in den Vertrag
 * gegangen. Eingesetzt wird nur an der Stelle der Luecke (op "luecke-fuellen").
 * entscheidung: der Knopf, der bis dahin gesperrt ist („Zustimmen“, „Passt“).
 */
function LueckenFelder({ luecken, gesperrt, onFuellen, entscheidung }) {
  const [werte, setWerte] = useState({});
  if (!luecken.length || !onFuellen) return null;
  const einsetzen = async (luecke) => {
    const wert = String(werte[luecke] || '').trim();
    if (!wert) {
      toast.error(`Bitte einen Wert für „${lueckenName(luecke)}“ eintragen.`);
      return;
    }
    const daten = await onFuellen(luecke, wert);
    if (daten) setWerte((v) => ({ ...v, [luecke]: '' }));
  };
  return (
    <Zeile label="Offen" oben={10}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {luecken.map((luecke) => (
          <form key={luecke} onSubmit={(e) => { e.preventDefault(); einsetzen(luecke); }}
            style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: T.tMeta, fontWeight: 600, color: T.ink, minWidth: 0 }}>{lueckenName(luecke)}</span>
            <input value={werte[luecke] || ''} disabled={gesperrt}
              onChange={(e) => setWerte((v) => ({ ...v, [luecke]: e.target.value }))}
              aria-label={`Wert für ${lueckenName(luecke)}`} placeholder="Wert eintragen"
              style={{ ...feldStil, width: 'auto', flex: '0 1 170px', minWidth: 90, padding: '4px 8px', fontSize: T.tMeta }} />
            <button type="submit" disabled={gesperrt} style={{ ...knopfStil(), padding: '4px 10px', ...aus(gesperrt) }}>
              Übernehmen
            </button>
          </form>
        ))}
        <div style={{ fontSize: T.tMikro, color: T.muted, lineHeight: 1.5 }}>
          Oder im Gespräch nennen – die KI setzt den Wert ein. „{entscheidung}“ geht erst danach.
        </div>
      </div>
    </Zeile>
  );
}

function Absaetze({ absaetze }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {(absaetze || []).map((absatz, i) => (
        // Fliesstext bekommt im Vertrag keine eigene Nummer - hier eingerueckt.
        <p key={i} style={{
          margin: 0, fontSize: T.tText, lineHeight: 1.65, color: T.ink,
          paddingLeft: absatz.art === 'fliesstext' ? 12 : 0,
        }}><TextMitLuecken text={absatz.text} /></p>
      ))}
    </div>
  );
}

// Unter dem Kopf eine Haarlinie: Kopf und Zeilen lesen sich dann wie ein
// Eintrag in einer Änderungsliste.
function Kopf({ nummer, titel, marke }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      paddingBottom: 10, marginBottom: 10, borderBottom: `1px solid ${T.hairline}`,
    }}>
      {nummer ? <NummerKreis>{nummer}</NummerKreis> : null}
      <div style={{ flex: 1, minWidth: 0, fontSize: T.tTitel, fontWeight: 700, color: T.navy, lineHeight: 1.4 }}>
        {titel}
      </div>
      {marke ? <span style={markeStil(marke.schrift, marke.rand, marke.grund)}>{marke.text}</span> : null}
    </div>
  );
}

/** Hinweise, die zu einer Entscheidung fuehren - nur bei offenen Entwuerfen mit Knopf. */
function Hinweise({ passus, parteien, frei, sperre, beschaeftigt, imVertrag, onAngleichen, onZuErsetzung, onVerwerfen }) {
  const abweichend = abweichendeParteien(passusText(passus), parteien);
  return (
    <>
      {/* Ohne KI am Vertrag geprueft (src/lib/imVertrag.js, seit 29.09.2026):
          Steht der Wortlaut schon so im Vertrag, stuende er nach dem
          Einfuegen doppelt darin. Der Hinweis der KI ("beruehrt") steht dann
          mit in dieser Zeile statt in einer eigenen. */}
      {imVertrag ? (
        <Hinweis ton="gefahr" titel="Steht schon im Vertrag:" aktion={frei ? (
          <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
            <button type="button" disabled={sperre} onClick={onVerwerfen}
              title="Die Ergänzung verwerfen - der Vertrag regelt das schon"
              style={{ ...knopfStil(), ...aus(sperre) }}>
              <XIcon size={12} /> Verwerfen
            </button>
            <button type="button" disabled={sperre} onClick={onZuErsetzung}
              title="Soll die bestehende Regelung anders lauten, tauscht Ersetzen die Stelle aus, statt eine zweite dazuzusetzen."
              style={{ ...knopfStil(), borderColor: T.gefahrRand, color: T.gefahr, ...aus(sperre) }}>
              <Replace size={12} /> {beschaeftigt ? 'Sucht die Stelle …' : 'Stattdessen ersetzen'}
            </button>
          </span>
        ) : null}>
          {imVertragText(imVertrag, passus.absaetze?.length || 1)}
          {passus.beruehrt ? ` · ${passus.beruehrt}` : ''}
        </Hinweis>
      ) : passus.beruehrt ? (
        <Hinweis ton="gefahr" titel="Doppelt geregelt:" aktion={frei ? (
          <button type="button" disabled={sperre} onClick={onZuErsetzung}
            title="Die Ergänzung käme zum Vertrag dazu, beide Regelungen stünden darin. Ersetzen tauscht die Stelle stattdessen aus."
            style={{ ...knopfStil(), borderColor: T.gefahrRand, color: T.gefahr, ...aus(sperre) }}>
            <Replace size={12} /> {beschaeftigt ? 'Sucht die Stelle …' : 'Stattdessen ersetzen'}
          </button>
        ) : null}>
          {passus.beruehrt}
        </Hinweis>
      ) : null}

      {abweichend.length ? (
        <Hinweis titel="Parteien heißen im Vertrag anders:" aktion={frei ? (
          <button type="button" disabled={sperre} onClick={onAngleichen} style={{ ...knopfStil(), ...aus(sperre) }}>
            <Sparkles size={11} /> {beschaeftigt ? 'Gleicht an …' : 'Angleichen'}
          </button>
        ) : null}>
          {abweichendText(abweichend)}
        </Hinweis>
      ) : null}

      {/* Bewusst "bitte pruefen": Das Modell kennzeichnet gelegentlich auch
          Werte, die genannt wurden. Zu viel zu warnen ist die sichere
          Richtung - die Beschriftung darf dann aber keine Genauigkeit
          behaupten, die sie nicht hat. */}
      {passus.angenommen?.length > 0 ? (
        <Hinweis titel="Von der KI eingesetzt, bitte prüfen:">{passus.angenommen.join(' · ')}</Hinweis>
      ) : null}

      {passus.stand === 'ueberarbeiten' && passus.hinweis ? (
        <Hinweis titel="Notiz:">{passus.hinweis}</Hinweis>
      ) : null}
    </>
  );
}

/** Wie oft der Passus ueberarbeitet wurde, die Begruendung (eingeklappt). Die
 *  Stelle steht seit 24.10.2026 oben in der Karte (StelleZeile). */
function Hintergrund({ passus }) {
  const [offen, setOffen] = useState(false);
  const fassungen = passus.fassungen?.length || 0;
  if (!fassungen && !passus.begruendung) return null;
  return (
    <div style={{ marginTop: 12, paddingTop: 9, borderTop: `1px solid ${T.hairline}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', rowGap: 4 }}>
        <span style={{ flex: '1 1 200px', minWidth: 0, fontSize: T.tMikro, color: T.muted }}>
          {fassungen ? `${fassungen}× überarbeitet` : ''}
        </span>
        {passus.begruendung ? (
          <Aufklapper offen={offen} onClick={() => setOffen((v) => !v)}>Begründung</Aufklapper>
        ) : null}
      </div>
      {offen ? (
        <div style={{ marginTop: 8, fontSize: T.tMeta, color: T.body, lineHeight: 1.6 }}>{passus.begruendung}</div>
      ) : null}
    </div>
  );
}

function Pfeile({ istErste, istLetzte, gesperrt, onVerschieben }) {
  return (
    <>
      <button type="button" title="Nach oben" aria-label="Nach oben" disabled={istErste || gesperrt}
        onClick={() => onVerschieben(-1)} style={{ ...iconKnopfStil, ...aus(istErste || gesperrt) }}>
        <ArrowUp size={12} />
      </button>
      <button type="button" title="Nach unten" aria-label="Nach unten" disabled={istLetzte || gesperrt}
        onClick={() => onVerschieben(1)} style={{ ...iconKnopfStil, ...aus(istLetzte || gesperrt) }}>
        <ArrowDown size={12} />
      </button>
    </>
  );
}

/** Das Feld "Was soll anders werden?" unter der Karte. */
function UeberarbeitenFeld({ passus, gesperrt, beschaeftigt, kiBereit, onUeberarbeiten, onVormerken, onSchliessen }) {
  const [text, setText] = useState(passus.hinweis || '');
  const kiGesperrt = gesperrt || !kiBereit;
  return (
    <div style={{ marginTop: 10 }}>
      <textarea
        value={text}
        autoFocus
        onChange={(e) => setText(e.target.value)}
        aria-label={`Was an „${passus.titel || 'dieser Ergänzung'}“ anders werden soll`}
        placeholder={UEBERARBEITEN_PLATZHALTER}
        rows={3}
        style={{ ...feldStil, resize: 'vertical', fontSize: T.tMeta }}
      />
      <div style={{ display: 'flex', gap: 12, marginTop: 7, alignItems: 'center', flexWrap: 'wrap' }}>
        <button type="button" disabled={kiGesperrt}
          onClick={async () => {
            const hinweis = text.trim();
            if (!hinweis) {
              toast.error('Bitte kurz beschreiben, was anders werden soll.');
              return;
            }
            const daten = await onUeberarbeiten(hinweis);
            if (daten) {
              onSchliessen();
              toast.success('Neu formuliert.');
            }
          }}
          style={{ ...knopfStil(true), ...aus(kiGesperrt) }}>
          <Sparkles size={12} /> {beschaeftigt ? 'Die KI schreibt …' : 'Neu formulieren'}
        </button>
        <button type="button" disabled={gesperrt} onClick={onSchliessen} style={{ ...textKnopfStil, ...aus(gesperrt) }}>
          Abbrechen
        </button>
        <span style={{ flex: 1 }} />
        <button type="button" disabled={gesperrt} onClick={() => onVormerken(text)}
          title="Ohne KI: die Karte als „Vorgemerkt“ markieren, der Hinweis bleibt stehen"
          style={{ ...textKnopfStil, ...aus(gesperrt) }}>
          Nur vormerken
        </button>
      </div>
    </div>
  );
}

/**
 * Eine Ergaenzung (Passus) - offen oder freigegeben.
 *
 * gesperrt: irgendeine Aktion laeuft, auch eine KI-Runde. Sonst schriebe ein
 * Klick auf denselben Stand, den die Runde gleich zurueckschreibt
 * (Code-Review 23.09.2026).
 */
export function ErgaenzungKarte({
  passus, nummer, parteien, gesperrt, beschaeftigt, kiBereit, istErste, istLetzte, imVertrag = null,
  onStand, onUeberarbeiten, onAngleichen, onZuErsetzung, onVerschieben, onSpringe, onLueckeFuellen = null,
}) {
  const [feldOffen, setFeldOffen] = useState(false);
  const frei = passus.stand === 'passt';
  const kiSperre = gesperrt || !kiBereit;
  // Mit offener Luecke kein „Passt“ - sonst kaeme „[Frist]“ in den Vertrag.
  const luecken = lueckenDerErgaenzung(passus);

  return (
    <div style={{
      ...karteStil, padding: '14px 16px 12px',
      borderColor: feldOffen ? T.orange : T.randWeich,
      boxShadow: mitStrich(frei ? T.gut : ''),
      opacity: gesperrt && !beschaeftigt ? 0.75 : 1,
      transition: 'opacity 150ms, border-color 150ms',
    }}>
      <Kopf nummer={nummer} titel={passus.titel || 'Ohne Titel'} marke={STAND_MARKE[passus.stand]} />
      {/* Erst freigegeben steht der Passus im Dokument - vorher ginge der
          Sprung in die Vorschau ins Leere. */}
      <StelleZeile {...ergaenzungsOrt(passus)} onZeigen={frei && onSpringe ? onSpringe : null} />
      <Zeile label="Neu">
        <div style={{ ...wortlautStil, borderLeft: `2px solid ${T.gutRand}` }}>
          <Absaetze absaetze={passus.absaetze} />
        </div>
      </Zeile>
      <LueckenFelder luecken={luecken} gesperrt={gesperrt} onFuellen={onLueckeFuellen} entscheidung="Passt" />
      <Hinweise passus={passus} parteien={parteien} frei={!frei} sperre={kiSperre} beschaeftigt={beschaeftigt}
        imVertrag={imVertrag} onVerwerfen={() => onStand('verworfen')}
        onAngleichen={onAngleichen} onZuErsetzung={onZuErsetzung} />
      <Hintergrund passus={passus} />

      {feldOffen ? (
        <UeberarbeitenFeld passus={passus} gesperrt={gesperrt} beschaeftigt={beschaeftigt} kiBereit={kiBereit}
          onUeberarbeiten={onUeberarbeiten}
          onVormerken={(hinweis) => { onStand('ueberarbeiten', hinweis); setFeldOffen(false); }}
          onSchliessen={() => setFeldOffen(false)} />
      ) : (
        <div style={{ display: 'flex', gap: 6, marginTop: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          {frei ? (
            <button type="button" disabled={gesperrt} onClick={() => onStand('entwurf')}
              style={{ ...textKnopfStil, ...aus(gesperrt) }}>
              <Undo2 size={12} /> Zurück zu Entwurf
            </button>
          ) : (
            <>
              <button type="button" disabled={gesperrt || luecken.length > 0} onClick={() => onStand('passt')}
                title={luecken.length ? 'Erst die offene Angabe ausfüllen' : undefined}
                style={{ ...knopfStil(true), ...aus(gesperrt || luecken.length > 0) }}>
                <Check size={12} /> Passt
              </button>
              <button type="button" disabled={gesperrt} onClick={() => setFeldOffen(true)}
                style={{ ...knopfStil(), ...aus(gesperrt) }}>
                <Pencil size={12} /> Überarbeiten
              </button>
              <button type="button" disabled={gesperrt} onClick={() => onStand('verworfen')}
                style={{ ...textKnopfStil, marginLeft: 6, ...aus(gesperrt) }}>
                <XIcon size={12} /> Verwerfen
              </button>
            </>
          )}
          <span style={{ flex: 1 }} />
          <Pfeile istErste={istErste} istLetzte={istLetzte} gesperrt={gesperrt} onVerschieben={onVerschieben} />
        </div>
      )}
    </div>
  );
}

/** Verworfen - knapp, mit dem Weg zurueck und dem endgueltigen Loeschen. */
export function VerworfeneKarte({ passus, gesperrt, onStand, onLoeschen }) {
  return (
    <div style={{ ...karteStil, padding: '11px 14px', background: T.grund, boxShadow: 'none' }}>
      <div style={{ fontSize: T.tMeta, fontWeight: 700, color: T.muted, marginBottom: 4 }}>
        {passus.titel || 'Ohne Titel'}
      </div>
      <div style={{
        fontSize: T.tMeta, color: T.muted, lineHeight: 1.55,
        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
      }}>
        {(passus.absaetze || []).map((a) => a.text).join(' ')}
      </div>
      <div style={{ display: 'flex', gap: 14, marginTop: 8 }}>
        <button type="button" disabled={gesperrt} onClick={() => onStand('entwurf')}
          style={{ ...textKnopfStil, ...aus(gesperrt) }}>
          <Undo2 size={12} /> Zurückholen
        </button>
        <button type="button" disabled={gesperrt} onClick={onLoeschen}
          style={{ ...textKnopfStil, color: T.gefahr, ...aus(gesperrt) }}>
          <Trash2 size={12} /> Löschen
        </button>
      </div>
    </div>
  );
}

/** Eine Ersetzung: bisheriger Wortlaut, neuer Wortlaut, Zustimmung. */
// zugehoerig: die Ergänzung, zu der diese Ersetzung gehört ({ titel, nummer }),
// sonst null. Seit 29.09.2026 fällt sie mit ihr, wenn die Ergänzung verworfen wird.
// ort: wo sie im Vertrag greift (ersetzungsOrt in src/lib/imVertrag.js), null =
// nicht gefunden.
export function ErsetzungKarte({
  aenderung, nummer, gesperrt, onStand, onSpringe, zugehoerig = null, ort = null, onLueckeFuellen = null,
}) {
  const [begruendungOffen, setBegruendungOffen] = useState(false);
  const istZu = aenderung.stand === 'zugestimmt';
  const istAb = aenderung.stand === 'abgelehnt';
  const luecken = istAb ? [] : lueckenDerErsetzung(aenderung);
  const marke = istZu
    ? { text: 'Zugestimmt', schrift: T.gut, rand: T.gutRand, grund: T.gutGrund }
    : (istAb ? { text: 'Abgelehnt', schrift: T.muted, rand: T.rand, grund: T.flaeche } : null);

  return (
    <div style={{
      ...karteStil, padding: '14px 16px 12px',
      boxShadow: mitStrich(istZu ? T.gut : ''),
      opacity: istAb ? 0.6 : 1,
    }}>
      <Kopf nummer={nummer} titel={aenderung.art === 'streichen' ? 'Streichen' : 'Ersetzen'} marke={marke} />

      {/* Ersetzungen tragen keine Kennung im Dokument - der Sprung sucht den
          neuen, dann den alten Wortlaut. Ohne gefundene Stelle nur nach dem
          Zustimmen (dann womöglich schon im Vertrag). */}
      {ort ? (
        <StelleZeile
          wo={ortText(ort)}
          zusatz={ort.gefunden === 'neu' ? 'steht schon im Vertrag' : (ort.mehrdeutig ? 'steht mehrfach im Vertrag' : '')}
          onZeigen={onSpringe || null}
        />
      ) : (
        <StelleZeile fehlt wo="Im Vertrag nicht gefunden" zusatz="Wortlaut prüfen"
          onZeigen={istZu && onSpringe ? onSpringe : null} />
      )}

      {zugehoerig ? (
        <Zeile label="Bezug">
          <div style={{ fontSize: T.tMeta, color: T.body, lineHeight: '19px' }}>
            Ergänzung {zugehoerig.nummer ? `${zugehoerig.nummer} · ` : ''}„{zugehoerig.titel || 'ohne Titel'}“
            {istAb && aenderung.abgelehntMit ? (
              <span style={{ color: T.muted }}> – mit ihr verworfen; kommt als Entwurf zurück, wenn du sie zurückholst.</span>
            ) : null}
          </div>
        </Zeile>
      ) : null}

      <Zeile label="Bisher">
        <div style={{
          ...wortlautStil, borderLeft: `2px solid ${T.gefahrRand}`, color: T.muted,
          textDecoration: 'line-through', textDecorationColor: T.gefahr,
        }}>{aenderung.fundstelle}</div>
      </Zeile>
      {aenderung.art === 'ersetzen' ? (
        <Zeile label="Neu">
          <div style={{ ...wortlautStil, borderLeft: `2px solid ${T.gutRand}` }}><TextMitLuecken text={aenderung.neu} /></div>
        </Zeile>
      ) : null}

      <LueckenFelder luecken={luecken} gesperrt={gesperrt} onFuellen={onLueckeFuellen} entscheidung="Zustimmen" />

      {aenderung.begruendung ? (
        <div style={{ marginTop: 12, paddingTop: 9, borderTop: `1px solid ${T.hairline}` }}>
          <Aufklapper offen={begruendungOffen} onClick={() => setBegruendungOffen((v) => !v)}>Begründung</Aufklapper>
          {begruendungOffen ? (
            <div style={{ marginTop: 8, fontSize: T.tMeta, color: T.body, lineHeight: 1.6 }}>{aenderung.begruendung}</div>
          ) : null}
        </div>
      ) : null}

      <div style={{ display: 'flex', gap: 6, marginTop: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        {istZu ? (
          <button type="button" disabled={gesperrt} onClick={() => onStand('entwurf')}
            style={{ ...textKnopfStil, ...aus(gesperrt) }}>
            <Undo2 size={12} /> Zustimmung zurücknehmen
          </button>
        ) : (
          <>
            <button type="button" disabled={gesperrt || luecken.length > 0} onClick={() => onStand('zugestimmt')}
              title={luecken.length ? 'Erst die offene Angabe ausfüllen' : undefined}
              style={{ ...knopfStil(true), ...aus(gesperrt || luecken.length > 0) }}>
              <Check size={12} /> Zustimmen
            </button>
            {!istAb ? (
              <button type="button" disabled={gesperrt} onClick={() => onStand('abgelehnt')}
                style={{ ...knopfStil(), ...aus(gesperrt) }}>
                <XIcon size={12} /> Ablehnen
              </button>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

/** Kurzer Hinweis ueber der Liste der Ersetzungen - eine ruhige Zeile, kein Kasten. */
export function ErsetzungenHinweis() {
  return (
    <div style={{ fontSize: T.tMeta, color: T.muted, lineHeight: 1.55 }}>
      Ersetzungen ändern bestehenden Vertragstext – nur in diesem Vorgang und erst nach deiner Zustimmung.
    </div>
  );
}
